import React from 'react';
import { createUseStyles } from 'react-jss';
import cx from 'classnames';
import { Theme } from '_lib/theme';
import daysOfMonth from '../_lib/helpers/daysOfMonth';
import isDateInPast from '../_lib/helpers/isDateInPast';

const useStyles = createUseStyles((theme: Theme) => ({
  noTransition: {
    transition: '0s !important',
  },

  container: {
    display: 'flex',
    flexWrap: 'wrap',
    flex: 1,
    background: '#fff',
  },
  date: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    minWidth: `${100 / 7}%`,
    position: 'relative',

    fontSize: 12,
    fontFamily: theme.typography.fontFamily,
    color: '#282C37',
    cursor: 'pointer',

    '&:hover div': {
      // dateInner
      color: '#fff',
      fontWeight: 700,
    },

    '&:hover span': {
      // dateInner
      opacity: 1,
      transform: 'scale(1)',
    },
  },
  dateDisabled: {
    pointerEvents: 'none',
    opacity: 0.5,
  },
  dateLarge: {
    fontSize: 16,
  },
  dateBlank: {
    pointerEvents: 'none',
    color: '#eaeaea',
  },
  dateActive: {
    color: '#fff',
    fontWeight: 700,
  },
  dateInner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 25,
    height: 25,
    borderRadius: '50%',
    zIndex: 1,
  },
  dateBall: {
    position: 'absolute',
    width: 25,
    height: 25,
    borderRadius: '50%',
    background: '#333',

    transition: '0.2s',
    transform: 'scale(0.5)',
    opacity: 0,
    zIndex: 0,
  },
  dateBallLarge: {
    width: 32,
    height: 32,
  },
  dateBallActive: {
    background: '#ff3c59',
    transform: 'scale(1)',
    opacity: 1,
  },
  availableMark: {
    width: '100%',
    position: 'absolute',
    bottom: 3,
  },
  availableMarkInner: {
    width: 4,
    height: 4,
    margin: 'auto',
    borderRadius: '50%',
    backgroundColor: theme.colors.primary,
  },
}));

type Props = {
  date: Date | string;
  onDateChange: (date: Date) => void;
  showActiveDate: boolean;
  availableDates?: Date[];
  size?: 'small' | 'large';
  allowPastSelection?: boolean;
};

const CalendarBody = (props: Props) => {
  const {
    onDateChange,
    showActiveDate,
    availableDates = [],
    size = 'small',
    date,
    allowPastSelection = false,
  } = props;
  const classes = useStyles();
  const days = daysOfMonth(new Date(date), availableDates);

  return (
    <div className={cx(classes.container)}>
      {days.map(({ dayInMonth, month, active, blank, rawDate, available }) => (
        <div
          key={dayInMonth + month}
          className={cx(classes.date, {
            [classes.dateBlank]: blank,
            [classes.dateLarge]: size === 'large',
            [classes.dateDisabled]:
              !allowPastSelection && isDateInPast(rawDate),
          })}
          onClick={() => onDateChange(rawDate)}
        >
          <span
            className={cx(classes.dateBall, {
              [classes.dateBallActive]: active && showActiveDate,
              [classes.dateBallLarge]: size === 'large',
            })}
          />
          <div
            className={cx(classes.dateInner, {
              [classes.dateActive]: active && showActiveDate,
              [classes.dateBallLarge]: size === 'large',
            })}
          >
            {dayInMonth}
          </div>
          {!blank && available && !active ? (
            <div className={classes.availableMark}>
              <div className={classes.availableMarkInner} />
            </div>
          ) : null}
        </div>
      ))}
    </div>
  );
};

export default CalendarBody;
